import { useEffect } from 'react'
import { usePreparedSchedule } from 'screens/Schedule/usePreparedSchedule'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { BrandPlanScheduleContent, SuggestionRequestStatus } from 'types'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useIsGenerationPaused } from 'hooks/useAutopilotPausedBanner/useIsGenerationPaused'
import { useIsPublishingPaused } from 'hooks/useAutopilotPausedBanner/useIsPublishingPaused'
import { ScheduleStatus } from 'screens/Schedule/Types'
import { useIsPostPromptsEnabled } from 'screens/Schedule/PostPromptsSection/useIsPostPromptsEnabled'

dayjs.extend(utc)

interface Props {
  scheduleId: number
}

interface ContentCounts {
  posted: number
  failed: number
  total: number
}

function countContents(contents: BrandPlanScheduleContent[] | undefined): ContentCounts {
  if (!contents) return { posted: 0, failed: 0, total: 0 }

  let posted = 0
  let failed = 0
  let total = 0
  for (const c of contents) {
    const file = c.almanac_file
    if (!file) continue
    total += 1
    if (
      file.post_status === PostStatusPropertyValue.Posted ||
      file.scheduled_post_targets?.some((t) => t.status === 'published')
    ) {
      posted += 1
    }
    if (
      file.post_status === PostStatusPropertyValue.Failed ||
      file.scheduled_post_targets?.some((t) => t.status === 'failed')
    ) {
      failed += 1
    }
  }
  return { posted, failed, total }
}

export const useScheduleStatus = ({ scheduleId }: Props): ScheduleStatus => {
  const isPostPromptsEnabled = useIsPostPromptsEnabled({ scheduleId })
  const queryClient = useQueryClient()
  const workspace = useCurrentWorkspace()
  const isGenerationPaused = useIsGenerationPaused()
  const isPublishingPaused = useIsPublishingPaused()
  const { data: schedule } = usePreparedSchedule({ scheduleId })

  useEffect(() => {
    queryClient.invalidateQueries(['schedules', 'detail', scheduleId])
  }, [isPublishingPaused, isGenerationPaused, queryClient, scheduleId])

  const counts = countContents(schedule?.contents)
  const generationStatus = schedule?.status
  const scheduledStatus = schedule?.scheduled_status

  const isApprovalsEnabled = !!workspace?.post_approval_enabled
  const isGenerating =
    generationStatus === SuggestionRequestStatus.Pending || generationStatus === SuggestionRequestStatus.Processing
  const hasContents = (schedule?.contents?.length ?? 0) > 0
  const allContentsTerminal = counts.total > 0 && counts.posted + counts.failed >= counts.total
  const isMostlyPosted = counts.posted >= counts.total / 2
  const hasOutOfWindowContents = (schedule?.out_of_window_contents_count ?? 0) > 0
  const hasUnreviewedContents = (schedule?.unreviewed_contents_count ?? 0) > 0
  const isSentForApproval = !!schedule?.sent_for_approval

  if (isGenerating && hasContents && isSentForApproval) {
    return isGenerationPaused ? 'paused' : 'generating'
  }

  if (scheduledStatus === 'published' || allContentsTerminal) {
    return isMostlyPosted ? 'posted' : 'failed'
  }

  if (scheduledStatus === 'publishing') {
    return isPublishingPaused ? 'paused' : 'posting'
  }

  if (scheduledStatus === 'paused' || isGenerationPaused || isPublishingPaused) {
    return 'paused'
  }

  if (isPostPromptsEnabled && hasOutOfWindowContents) {
    return 'rescheduled'
  }

  if (isApprovalsEnabled && isSentForApproval && !hasUnreviewedContents) {
    return 'approved'
  }

  if (isApprovalsEnabled && hasUnreviewedContents && isSentForApproval) {
    return 'review'
  }

  if (!isApprovalsEnabled && isSentForApproval) {
    return 'posting'
  }

  return 'ready'
}
